import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GitaHeader } from '../components/GitaHeader/GitaHeader';
import ChapterList from '../components/ChapterList/ChapterList';
import SlokaList from '../components/SlokaList/SlokaList';
import SlokaSummary from '../components/SlokaSummary/SlokaSummary';
import { setReaderDocumentTitle } from '../utils/seo';
import '../App.css';

const MAX_CHAPTER = 18;

function parsePositiveInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? n : fallback;
}

export default function Reader() {
  const { chapter, sloka } = useParams();
  const navigate = useNavigate();

  const chapterNumber = (() => {
    const n = parsePositiveInt(chapter, 1);
    return Math.min(n, MAX_CHAPTER);
  })();

  const slokaNumber = parsePositiveInt(sloka, 1);

  const [language, setLanguage] = useState('en');
  const [audio, setAudio] = useState('male');

  useLayoutEffect(() => {
    const cRaw = parsePositiveInt(chapter, 1);
    const sRaw = parsePositiveInt(sloka, 1);
    const c = Math.min(cRaw, MAX_CHAPTER);
    const s = sRaw;
    if (String(c) !== String(chapter) || String(s) !== String(sloka)) {
      navigate(`/read/${c}/${s}`, { replace: true });
    }
  }, [chapter, sloka, navigate]);

  useEffect(() => {
    setReaderDocumentTitle(chapterNumber, slokaNumber, language);
  }, [chapterNumber, slokaNumber, language]);

  const handleSelectChapter = (number) => {
    navigate(`/read/${number}/1`);
  };

  const handleSelectSloka = (number) => {
    navigate(`/read/${chapterNumber}/${number}`);
  };

  return (
    <div className="gita-app-root">
      <div className="gita-shell">
        <div className="gita-header-wrap no-print">
          <GitaHeader
            language={language}
            audio={audio}
            onLanguageChange={setLanguage}
            onAudioChange={setAudio}
          />
        </div>

        <main id="main-content" className="gita-main" tabIndex={-1}>
          <section className="gita-panel gita-panel--chapters no-print" aria-label="Chapters">
            <ChapterList
              language={language}
              selectedChapter={chapterNumber}
              onSelectChapterNumber={handleSelectChapter}
            />
          </section>

          <section className="gita-panel gita-panel--slokas no-print" aria-label="Verses in chapter">
            {chapterNumber ? (
              <SlokaList
                language={language}
                chapterNumber={chapterNumber}
                selectedSloka={slokaNumber}
                onSelectSlokaNumber={handleSelectSloka}
              />
            ) : null}
          </section>

          <section className="gita-panel gita-panel--reading print-main" aria-label="Selected verse">
            {chapterNumber && slokaNumber ? (
              <SlokaSummary
                audio={audio}
                language={language}
                chapterNumber={chapterNumber}
                slokaNumber={slokaNumber}
              />
            ) : null}
          </section>
        </main>

        <footer className="gita-footer no-print">
          <span className="gita-footer__om" aria-hidden="true">
            ॐ
          </span>
          Resources:{' '}
          <a href="https://thegita.org.in/images/chapters/" target="_blank" rel="noopener noreferrer">
            Chapter art
          </a>
          {' · '}
          <a href="https://thegita.org.in/audio/" target="_blank" rel="noopener noreferrer">
            Audio
          </a>
          {' · '}
          <a href="https://api.thegita.org.in" target="_blank" rel="noopener noreferrer">
            API
          </a>
          {' · '}
          <a href="https://ux.thegita.org.in" target="_blank" rel="noopener noreferrer">
            Study assistant
          </a>
        </footer>
      </div>
    </div>
  );
}
